import { Component, OnInit, OnDestroy } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { ApiDataService } from "src/app/shared/services/api.service";
import { UntypedFormGroup, Validators, UntypedFormControl } from "@angular/forms";
import { Tutor } from "src/app/shared/models/tutor-base";
import { SearchBase } from "src/app/shared/models/search-base";
import { Student } from "src/app/shared/models/student-base";
import { ToastrService } from "ngx-toastr";
import {
  SuccessMessages,
  MASK,
  ErrorMessages,
} from "src/app/shared/constants/utility.constants";
import { PathRouteConstants } from "src/app/shared/constants/app-route.constants";
import { formatDate, Location } from "@angular/common";
import { School } from "src/app/shared/models/school";
import { StudentConstants } from "../constants/student.constants";
import { takeUntil } from "rxjs/operators";
import { Subject } from "rxjs";
import { StudentUtility } from "../services/student-utility-services";
import { ConfirmDialogComponent } from "../../confirm-dialog/confirm-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { FilesDeleteConfirmationComponent } from "../../files-delete-confirmation/files-delete-confirmation.component";

@Component({
    selector: "app-create-edit-student",
    templateUrl: "./create-edit-student.component.html",
    styleUrls: ["./create-edit-student.component.css"],
    standalone: false
})
export class CreateEditStudentComponent implements OnInit, OnDestroy {
  showFileInput: boolean = false;
  selectedFile: File | null = null; // Stores the selected file
  fileError: string | null = null;
  selectedFileBase64: string | null = null;
  ngUnsubscribe = new Subject <void>();
  studentId: number;
  studentToEdit: Student;
  allVolunteers: Tutor[];
  studentForm: UntypedFormGroup;
  editStudent: boolean;
  schoolList: School[];
  loading: boolean;
  searchObj: SearchBase = { SearchText: "" };
  grades: any;
  zones: any;
  pairingGroups = StudentConstants.PAIRING_GROUP;
  skillLevels = StudentConstants.SKILL_LEVELS;
  subsVolunteers: Tutor[] = [];
  filteredListCount = { count: 0 };
  selectedPrimaryVolunteers: Tutor[] = [];
  studentIds: number[] = [];
  textMask = MASK.TEXT_MASK;
  tutorList: any;
  schoolControl: UntypedFormControl = new UntypedFormControl();
  schoolOptions: School[];
  preferences: any;
  active: number;
  readingLevel = [
    { display: "Yes", value: 1 },
    { display: "No", value: 0 },
  ];
  pattern = "d{4}/d{4}";
  constructor(
    private route: ActivatedRoute,
    private apiService: ApiDataService,
    private toastrService: ToastrService,
    private router: Router,
    private dialog: MatDialog,
    public _location: Location
  ) {
    this.initializeForm();
  }

  initializeForm() {
    this.studentForm = new UntypedFormGroup({
      first_name: new UntypedFormControl("", [
        Validators.required,
        Validators.pattern(/^(?=.*\S).+$/),
      ]),
      second_name: new UntypedFormControl(""),
      last_name: new UntypedFormControl("", [
        Validators.required,
        Validators.pattern(/^(?=.*\S).+$/),
      ]),
      class_name: new UntypedFormControl("", [
        Validators.required,
        Validators.pattern(/^(?=.*\S).+$/),
      ]),
      school: new UntypedFormControl("", Validators.required),
      grade: new UntypedFormControl("", Validators.required),
      zone: new UntypedFormControl(""),
      second_grade_year: new UntypedFormControl(""),
      third_grade_year: new UntypedFormControl(""),
      fifth_grade_year: new UntypedFormControl(""),
      other_information: new UntypedFormControl(""),
      control_group: new UntypedFormControl(false),
      preference: new UntypedFormControl("", Validators.required),
      notes: new UntypedFormControl(""),
      is_reading_level: new UntypedFormControl(0),
      // Placement results are valid Reading Eggs breakpoints: 11, 21, 31, etc.
      placement_result: new UntypedFormControl(null, [
        Validators.min(11),
        Validators.max(999),
        Validators.pattern(/^(?:[0-9]+1)?$/)
      ]),
    });
  }

  ngOnInit() {
    this.loading = true;
    this.editParamsSubscribe();
    this.getSchools();
    this.getPreferences();
    this.getGrades();
    this.getZones();
    this.schoolControl.valueChanges.subscribe((value) => {
      this.schoolOptions = StudentUtility.filterOptions(
        this.schoolList,
        value,
        ["school_name"]
      );
    });
  }
  onFileChange(event: any): void {
    const file = event.target.files[0];

    if (file) {
      const fileType = file.type;

      // Validate file type
      if (fileType !== StudentConstants.PDF_FILE_TYPE) {
        this.fileError = StudentConstants.FILE_TYPE_ERROR;
        this.selectedFile = null; // Clear the selected file
        event.target.value = ""; // Clear the input value
      } else {
        this.fileError = null; // Clear the error message if valid
        this.selectedFile = file;
        this.convertFileToBase64(file);
      }
    }
  }

  // Convert the file to base64
  convertFileToBase64(file: File) {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => {
      this.selectedFileBase64 = reader.result as string;
    };
  }
  
  allowOnlyNumbers(event: KeyboardEvent) {
    if (!/^\d$/.test(event.key) &&
        !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(event.key)) {
      event.preventDefault();
    }
  }

  toggleFileInput(): void {
    this.showFileInput = !this.showFileInput;
  }

  deleteConsentForm(fileId: number): void {
    const dialogRef = this.dialog.open(FilesDeleteConfirmationComponent, {
      data: {
        title: StudentConstants.DEL_MODAL_TITLE,
        description: StudentConstants.DEL_MODAL_DEC,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.apiService.deleteConsentForm(fileId).subscribe(
          (response) => {
            // Success: Remove the file from the `files` array
            this.studentToEdit.files = this.studentToEdit.files.filter(
              (file) => file.id !== fileId
            );
            this.toastrService.success(response.message);
          },
          (error) => {
            alert(StudentConstants.DEL_FILE_FAILED_ALERT);
          }
        );
      }
    });
  }

  getFileNameFromUrl(fileUrl: string): string {
    try {
      // Extract the file name from the URL
      const urlParts = fileUrl.split("/");
      return decodeURIComponent(urlParts[urlParts.length - 1]);
    } catch {
      return StudentConstants.FILE_TYPE_ERR;
    }
  }

  editParamsSubscribe() {
    this.route.params
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((params) => {
        this.studentId = +params.id;
        if (this.studentId) {
          this.editStudent = true;
          this.getStudent();
        }
      });
  }

  getStudent() {
    this.apiService
      .getStudent(this.studentId)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((res) => {
        if (res) {
          this.studentToEdit = res;
          this.tutorList = res?.tutor;
          this.active = res.is_active;
          this.setForm();
        }
        this.loading = false;
      });
  }

  setForm() {
    this.studentForm.setValue({
      first_name: this.studentToEdit.first_name,
      second_name: this.studentToEdit.second_name,
      last_name: this.studentToEdit.last_name,
      class_name: this.studentToEdit.class_name,
      school: this.studentToEdit.school.id,
      grade: this.studentToEdit.grade.id,
      zone: this.studentToEdit.zone?.id ?? "",
      second_grade_year: this.studentToEdit.second_grade_year,
      third_grade_year: this.studentToEdit.third_grade_year,
      fifth_grade_year: this.studentToEdit.fifth_grade_year,
      other_information: this.studentToEdit.other_information,
      control_group: this.studentToEdit.control_group,
      preference: this.studentToEdit.preference?.id ?? "",  
      notes: this.studentToEdit.notes,
      is_reading_level: this.studentToEdit.is_reading_level,
      placement_result: this.studentToEdit.placement_result ?? null,
    });
    this.isControlCheck();
  }

  getSchools() {
    this.apiService
      .getSchoolList()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((res) => {
        if (res.length > 0) {
          this.schoolList = res;
          this.schoolOptions = res;
          if (!this.studentId) {
            this.loading = false;
          }
        }
      });
  }
  getPreferences() {
    this.apiService.getAllPreferences().subscribe((res) => {
      this.preferences = res;
    });
  }
  getGrades() {
    this.apiService.getAllGrades().subscribe((res) => {
      this.grades = res;
      this.grades = this.grades.sort((a, b) => {
        if (a.grade_level < b.grade_level) return -1;
        if (a.grade_level > b.grade_level) return 1;
        return 0;
      });
    });
  }
  getZones() {
    this.apiService.getAllZones().subscribe((res) => {
      this.zones = res;
    });
  }
  isControlCheck() {
    if (this.studentForm.get("grade").value === "5th") {
      this.studentForm.get("control_group").setValue(false);
      this.studentForm.get("control_group").disable();
    } else {
      this.studentForm.get("control_group").enable();
    }
  }

  updateStudent(student) {
    student.first_name = student.first_name.trim();
    student.second_name = student.second_name
      ? student.second_name.trim()
      : null;
    student.last_name = student.last_name.trim();
    student.class_name = student.class_name.trim();
      student.second_grade_year = student.second_grade_year
        ? student.second_grade_year.slice(0, 4) +
          "/" +
          student.second_grade_year.slice(4)
        : "";
      student.third_grade_year = student.third_grade_year
        ? student.third_grade_year.slice(0, 4) +
          "/" +
          student.third_grade_year.slice(4)
        : "";
      student.fifth_grade_year = student.fifth_grade_year
        ? student.fifth_grade_year.slice(0, 4) +
          "/" +
          student.fifth_grade_year.slice(4)
        : "";
      student.placement_result =
        student.placement_result === "" ||
        student.placement_result === null ||
        student.placement_result === undefined
          ? null
          : Number(student.placement_result);

          // If the inputed amount is under 0, and above 999, error message appears under the field
    if (
      student.placement_result !== null &&
      (student.placement_result < 0 || student.placement_result > 999)
    ) {
      this.toastrService.error("Placement result must be between 0 and 999.");
      this.loading = false;
      return;
    }

    // Find the selected school in schoolOptions
    const selectedSchool = this.schoolOptions.find(
      (school) => school.id === student.school
    );

    const studentData = {
      ...student,
      school_name: selectedSchool ? selectedSchool.school_name.trim() : null,
    };
               
    // Create a FormData object
    const formData = new FormData();
    formData.append("studentData", JSON.stringify(studentData)); // Append student data as a JSON string

    // Append the file to the FormData if it exists
    if (this.selectedFile) {
      formData.append("file", this.selectedFile, this.selectedFile.name);
    }

    this.loading = true;

    if (!this.studentId) {
      this.apiService
        .createStudent(formData) // Pass FormData to the API
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(
          (res) => {
            if (res) {
              this.toastrService.success(SuccessMessages.CREATE_STUDENT);
              this.router.navigate([PathRouteConstants.STUDENT_LIST]);
            }
          },
          (err) => {
            this.loading = false;
        const message =
          err?.error?.ResponseMessage ||
          err?.error?.statusMessage ||
          ErrorMessages.STUDENT_CREATE;

        this.toastrService.error(message);
          }
        );
    } else {
      this.apiService
        .updateStudent(this.studentId, formData) // Pass FormData to the API
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(
          (res) => {
            if (res) {
              this.navigateBack(SuccessMessages.UPDATE_STUDENT, "success");
            }
          },
          (err) => {
            this.loading = false;
            this.toastrService.error(ErrorMessages.STUDENT_UPDATE);
          }
        );
    }
  }

  
  

  navigateBack(message, status) {
    if (status === "success") {
      this.toastrService.success(message);
      if (this.active) {
        this.router.navigate([PathRouteConstants.STUDENT_LIST]);
      } else {
        this.router.navigate([PathRouteConstants.INACTIVE_STUDENT_LIST]);
      }
    } else {
      this.toastrService.error(message);
    }
    this.loading = false;
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next(null);
    this.ngUnsubscribe.complete();
  }
}
